// Date helpers
// -------------
// Shared date / time formatters for every UI surface. Three render
// shapes appear across the app:
//
//   formatDateWithLocale  — "5 March 2026"  long-form, used in vuln
//                            detail metadata and report headers.
//   formatLongDate        — "Wed Mar 5, 2026"  weekday + short month,
//                            used in the Dashboard summary card and
//                            the WebApplication "Last Test" panel.
//   formatShortDate       — "Mar 5, 2026"  month-day-year, used in
//                            the Dashboard vuln-row date columns.
//   formatDayMonth        — "5 Mar 2026"  day-month-year, used in
//                            the TestSelector dropdown labels.
//   formatTime            — "3:42 PM"  used wherever a clock time
//                            accompanies the date.
//
// Each helper accepts the same union type as formatDateWithLocale:
// number (treated as Unix seconds), string (parsed by Date()), or an
// already-parsed Date. Returns "" for falsy / unparseable input so
// call sites can render a placeholder without a guard.
//
// Was previously a parallel set of local helpers in Dashboard.tsx,
// TestDetails.tsx, TestSelector.tsx, with three independent copies
// of the months/days arrays and three slightly different epoch
// handling rules — easy to drift on a future format tweak.

import { EPOCH_TO_JS_DATE_CONVERSION } from "@/constants/definitions";

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type DateInput = number | string | Date | null | undefined;

// Parse the union input into a JS Date. number → epoch seconds (the
// shape used throughout the app's AppSync models); string → ISO or
// Date()-parseable; Date → returned as-is. Returns null when the
// input is falsy or yields an Invalid Date.
function parseDateInput(input: DateInput): Date | null {
  if (!input && input !== 0) return null;
  const parsed =
    input instanceof Date
      ? input
      : typeof input === "number"
      ? new Date(input * EPOCH_TO_JS_DATE_CONVERSION)
      : new Date(input);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

// a and b are javascript Date objects
export function dateDiffInDays(a: Date, b: Date): number {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export function formatDateWithLocale(date: DateInput): string {
  const parsed = parseDateInput(date);
  if (!parsed) return "";

  // "5 March 2026" — unambiguous regardless of locale
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
}

// "Wed Mar 5, 2026"
export function formatLongDate(date: DateInput): string {
  const parsed = parseDateInput(date);
  if (!parsed) return "";
  return `${DAYS_SHORT[parsed.getDay()]} ${MONTHS_SHORT[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()}`;
}

// "Mar 5, 2026"
export function formatShortDate(date: DateInput): string {
  const parsed = parseDateInput(date);
  if (!parsed) return "—";
  return `${MONTHS_SHORT[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()}`;
}

// "5 Mar 2026"
export function formatDayMonth(date: DateInput): string {
  const parsed = parseDateInput(date);
  if (!parsed) return "Unknown date";
  return `${parsed.getDate()} ${MONTHS_SHORT[parsed.getMonth()]} ${parsed.getFullYear()}`;
}

// "3:42 PM" — 12-hour clock with AM/PM suffix. Note: hour 0 (midnight)
// renders as "0:42 AM" rather than "12:42 AM"; preserved verbatim from
// the original local helpers in Dashboard / TestDetails. Fix separately
// if needed — outside the scope of the de-duplication refactor.
export function formatTime(date: DateInput): string {
  const parsed = parseDateInput(date);
  if (!parsed) return "";
  const hours = parsed.getHours();
  const hourString = hours > 12 ? (hours - 12).toString() : hours.toString();
  const amPM = hours < 12 ? "AM" : "PM";
  const minString = parsed.getMinutes().toString().padStart(2, "0");
  return `${hourString}:${minString} ${amPM}`;
}
